<template>
  <header class="relative z-50 shrink-0 h-16 px-4 sm:px-6 lg:px-8 flex items-center justify-between bg-base/80 backdrop-blur-xl border-b border-line">
    <!-- Page title -->
    <h1 class="text-lg sm:text-xl font-bold text-white truncate">{{ pageTitle }}</h1>

    <!-- Right actions -->
    <div class="flex items-center gap-3 sm:gap-4">
      <!-- Subscribe -->
      <a v-if="!isSubscribed" href="/subscribe" class="hidden sm:inline-flex">
        <button class="btn-gold">
          <i class="fas fa-crown"></i>
          <span class="hidden md:inline">Upgrade</span>
        </button>
      </a>

      <template v-if="isLoggedIn">
        <div class="relative" ref="dropdownMenu">
          <!-- Avatar trigger -->
          <button
            class="flex items-center gap-2.5 rounded-full pl-1 pr-1 sm:pr-3 py-1 hover:bg-surface-light transition-colors"
            @click.stop="toggleMenu"
          >
            <img :src="profilePicture" alt="User Avatar" class="h-9 w-9 rounded-full object-cover ring-2 ring-line" />
            <span class="hidden sm:block text-sm font-semibold text-white max-w-[120px] truncate">
              {{ profile?.username || "User" }}
            </span>
            <i class="hidden sm:block fas fa-chevron-down text-[10px] text-ink-muted"></i>
          </button>

          <!-- Dropdown -->
          <transition
            enter-active-class="transition duration-150 ease-out"
            enter-from-class="opacity-0 scale-95 -translate-y-1"
            enter-to-class="opacity-100 scale-100 translate-y-0"
            leave-active-class="transition duration-100 ease-in"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0 scale-95"
          >
            <div
              v-if="isMenuOpen"
              class="absolute right-0 mt-2 w-64 origin-top-right card overflow-hidden z-50"
            >
              <div class="flex items-center gap-3 p-4 border-b border-line bg-surface-light/40">
                <img :src="profilePicture" alt="User Avatar" class="h-12 w-12 rounded-full object-cover ring-2 ring-brand/40" />
                <div class="min-w-0">
                  <p class="font-bold text-white truncate">{{ profile?.username || "Username" }}</p>
                  <p class="text-xs text-ink-muted truncate">{{ profile?.email || "email" }}</p>
                </div>
              </div>
              <div class="p-1.5">
                <a href="/profile" class="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-ink-muted hover:text-white hover:bg-surface-light transition-colors">
                  <i class="fas fa-user-gear w-4 text-center text-brand"></i> Manage Account
                </a>
                <a href="/subscribe" class="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-ink-muted hover:text-white hover:bg-surface-light transition-colors">
                  <i class="fas fa-crown w-4 text-center text-brand"></i> Subscription
                </a>
                <button
                  class="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-ink-muted hover:text-white hover:bg-surface-light transition-colors text-left"
                  @click.prevent="logout"
                >
                  <i class="fas fa-arrow-right-from-bracket w-4 text-center text-brand"></i> Logout
                </button>
              </div>
            </div>
          </transition>
        </div>
      </template>

      <!-- Unauthenticated (rarely shown here, but kept as fallback) -->
      <div v-else class="flex items-center gap-2">
        <a href="/login" class="btn-ghost">Login</a>
        <a href="/register" class="btn-primary">Register</a>
      </div>
    </div>
  </header>
</template>

<script>
/* eslint-disable vue/multi-word-component-names */
import apiClient from "@/axiosConfig";
import { isAuthenticated } from "@/utils/auth";

export default {
  name: "Header",
  data() {
    return {
      isMenuOpen: false,
      profile: null,
      isSubscribed: false,
    };
  },
  computed: {
    isLoggedIn() {
      return isAuthenticated();
    },
    profilePicture() {
      if (!this.profile || !this.profile.id) return "/assets/profile/1.png";
      const userId = this.profile.id;
      const userImageIndex = (parseInt(userId.replace(/-/g, ""), 16) % 20) + 1;
      return `/assets/profile/${userImageIndex}.png`;
    },
    pageTitle() {
      const title = this.$route.meta.title || "Dashboard";
      return title.split("|")[0].trim();
    },
  },
  methods: {
    async fetchProfile() {
      if (!this.isLoggedIn) return;
      try {
        const response = await apiClient.get("/users/current");
        this.profile = response.data.user;
        this.isSubscribed = !!response.data.user.isSubscribed;
      } catch (error) {
        console.error("Failed to load profile information:", error);
      }
    },
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;
    },
    handleOutsideClick(event) {
      if (this.$refs.dropdownMenu && !this.$refs.dropdownMenu.contains(event.target)) {
        this.isMenuOpen = false;
      }
    },
    logout() {
      localStorage.removeItem("authToken");
      document.cookie = "authToken=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT;";
      this.$router.push("/login");
    },
  },
  mounted() {
    this.fetchProfile();
    document.addEventListener("click", this.handleOutsideClick);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleOutsideClick);
  },
};
</script>
